import { useMemo, useState } from "react";

import type { User } from "@sentry/react";
import { Button, Icon_Kebab, Menu } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import toast from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";

import { defaultPrivateSpaceName, defaultTeamSpaceName, webViewURL } from "../../config";
import useAccount from "../../hooks/useAccount";
import type { ProjectViewLink, Space } from "../../pages/ViewLinkListPage/ViewLinkListPage";
import { tabletSmallBreakpoint } from "../../utils/constants";
import { formatDate, formatDateCompressed } from "../../utils/helpers";
import useTracker from "../../utils/useTracker";
import { CollaborationPromo } from "../CollaborationPromo/CollaborationPromo";
import { MenuPortal } from "../Headers/components/ShareModal/components/MenuPortal";
import { UnpublishConfirmationModal } from "../UnpublishConfirmationModal/UnpublishConfirmationModal";
import { UserName } from "../UserName/UserName";

import style from "./ViewLinkList.module.css";

interface ViewLinkListProps {
  viewLinks: ProjectViewLink[];
  users: User[];
  isShortVersion?: boolean;
  isUnpublishHidden?: boolean;
}

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text).then(() => {
    toast.success("Copied to clipboard");
  });
};

const mapSpaceName = (space?: Space) => {
  if (!space) return "-";
  if (space.spaceName) return space.spaceName;
  if (space.spaceType === "private") return defaultPrivateSpaceName;
  return defaultTeamSpaceName;
};

export const ViewLinkList: React.FC<ViewLinkListProps> = ({
  viewLinks,
  users,
  isShortVersion,
  isUnpublishHidden,
}) => {
  const { data: accountData } = useAccount();
  const { teamID } = useParams();
  const tracker = useTracker();
  const navigate = useNavigate();

  const [unpublishViewLinkId, setUnpublishViewLinkId] = useState<string | null>(null);

  const mappedViewLinks = useMemo(() => {
    return viewLinks.map((viewLink) => {
      let username = "-";
      if (accountData?.id === viewLink.uploadedByUserId) {
        username = "Me";
      } else {
        const user = users.find((user) => user.userId === viewLink.uploadedByUserId);
        username = user?.name ?? user?.email ?? "-";
      }
      return {
        ...viewLink,
        spaceName: mapSpaceName(viewLink.space),
        uploadedByUserName: username,
      };
    });
  }, [viewLinks, accountData, users]);

  const showPromo =
    mappedViewLinks.length === 0 ||
    (!isShortVersion &&
      mappedViewLinks.length === 1 &&
      accountData &&
      mappedViewLinks?.[0]?.uploadedByUserId !== accountData.id);

  return (
    <>
      {mappedViewLinks.length > 0 && (
        <div>
          <div
            className={classNames(
              style.gridRow,
              style.header,
              isShortVersion ? style.gidRowShort : ""
            )}
          >
            {!isShortVersion && <div className={style.columnProject}>Project</div>}
            {!isShortVersion && <div className={style.columnProjectName}>Project name</div>}
            <div className={style.columnLink}>Link</div>
            <div className={style.columnCopy}></div>
            {!isShortVersion && <div className={style.columnLocation}>Location</div>}
            <div className={style.columnUpdatedAt}>Last updated</div>
            <div className={style.columnUpdatedBy}>Updated by</div>
            <div className={style.columnExtra}></div>
          </div>

          <div>
            {mappedViewLinks.map((viewLink) => (
              <div
                className={style.rowWrapper}
                key={viewLink.projectViewLinkId}
                data-testid="view-link-row"
              >
                <Link
                  to={"/v/" + viewLink.projectViewLinkId}
                  className={style.linkOverlay}
                  onClick={(e) => {
                    // avoid make the whole row clickable for mobile
                    if (window.innerWidth > tabletSmallBreakpoint) {
                      tracker.trackDashboardViewLinkClicked({
                        activeTeamId: teamID,
                        viewLinkId: viewLink.projectViewLinkId,
                        location: isShortVersion ? "project_page" : "dashboard",
                      });
                    } else {
                      e.preventDefault();
                    }
                  }}
                />
                <div
                  className={classNames(
                    style.gridRow,
                    style.row,
                    isShortVersion ? style.gidRowShort : ""
                  )}
                >
                  {!isShortVersion && (
                    <>
                      <div className={style.columnProjectName}>
                        <div>
                          <b>{viewLink.title}</b>
                        </div>
                      </div>

                      <div className={style.columnProject}>
                        <b>{viewLink.title}</b>
                        <div className={style.subtitle}>
                          {`${formatDateCompressed(viewLink.uploadedAt.toString())} · `}{" "}
                          <UserName name={viewLink.uploadedByUserName} />
                        </div>
                      </div>
                    </>
                  )}

                  <div
                    className={classNames([style.link, style.columnLink])}
                    title={`${webViewURL}/v/${viewLink.projectViewLinkId}`}
                  >
                    <div
                      className={style.url}
                    >{`${webViewURL}/v/${viewLink.projectViewLinkId}`}</div>
                    <div className={style.subtitle}>
                      {`${formatDate(viewLink.uploadedAt.toString())}`}
                      <span>·</span>
                      <UserName name={viewLink.uploadedByUserName} />
                    </div>
                  </div>

                  <div className={style.columnCopy}>
                    <Button
                      variant="borderless"
                      onClick={(e) => {
                        e.preventDefault();
                        tracker.trackDashboardViewLinkCopied({
                          activeTeamId: teamID,
                          viewLinkId: viewLink.projectViewLinkId,
                          location: isShortVersion ? "project_page" : "dashboard",
                        });
                        copyToClipboard(`${webViewURL}/v/${viewLink.projectViewLinkId}`);
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                  {!isShortVersion && (
                    <div className={style.columnLocation}>{viewLink.spaceName}</div>
                  )}

                  <div
                    className={classNames([style.date, style.columnUpdatedAt])}
                    title={formatDateCompressed(viewLink.uploadedAt.toString())}
                  >
                    {formatDateCompressed(viewLink.uploadedAt.toString())}
                  </div>
                  <div className={classNames([style.name, style.columnUpdatedBy])}>
                    <UserName name={viewLink.uploadedByUserName} />
                  </div>

                  <div className={classNames([style.columnExtra])}>
                    <MenuPortal
                      activator={
                        <Button
                          variant="borderless"
                          onClick={(e) => {
                            e.preventDefault();
                          }}
                        >
                          <Icon_Kebab />
                        </Button>
                      }
                      className={style.contextMenu}
                      onClick={(e) => {
                        e.preventDefault();
                      }}
                      onOpen={() => {
                        tracker.trackDashboardViewLinkContextMenuOpened({
                          activeTeamId: teamID,
                          viewLinkId: viewLink.projectViewLinkId,
                          location: isShortVersion ? "project_page" : "dashboard",
                        });
                      }}
                    >
                      <Menu.Item
                        closeOnClick
                        onClick={() => {
                          tracker.trackDashboardViewMenuActed({
                            action: "open_link",
                            activeTeamId: teamID,
                            viewLinkId: viewLink.projectViewLinkId,
                            location: isShortVersion ? "project_page" : "dashboard",
                          });
                          navigate("/v/" + viewLink.projectViewLinkId);
                        }}
                      >
                        <span>Open</span>
                      </Menu.Item>
                      <Menu.Item
                        onClick={(e) => {
                          e.stopPropagation();
                          tracker.trackDashboardViewMenuActed({
                            action: "open_link_in_new_tab",
                            activeTeamId: teamID,
                            viewLinkId: viewLink.projectViewLinkId,
                            location: isShortVersion ? "project_page" : "dashboard",
                          });
                          window.open(
                            window.location.origin + "/v/" + viewLink.projectViewLinkId,
                            "_blank"
                          );
                        }}
                      >
                        <span>Open in new tab</span>
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item
                        closeOnClick
                        onClick={() => {
                          tracker.trackDashboardViewMenuActed({
                            action: "copy_link",
                            activeTeamId: teamID,
                            viewLinkId: viewLink.projectViewLinkId,
                            location: isShortVersion ? "project_page" : "dashboard",
                          });
                          copyToClipboard(`${webViewURL}/v/${viewLink.projectViewLinkId}`);
                        }}
                      >
                        <span>Copy</span>
                      </Menu.Item>
                      {!isUnpublishHidden && (
                        <>
                          <Menu.Divider />
                          <Menu.Item
                            disabled={viewLink.space === undefined}
                            closeOnClick
                            onClick={() => {
                              tracker.trackDashboardViewMenuActed({
                                action: "unpublish",
                                activeTeamId: teamID,
                                viewLinkId: viewLink.projectViewLinkId,
                                location: isShortVersion ? "project_page" : "dashboard",
                              });
                              setUnpublishViewLinkId(viewLink.projectViewLinkId);
                            }}
                          >
                            <span className={style.alertButton}>Unpublish</span>
                          </Menu.Item>
                        </>
                      )}
                    </MenuPortal>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {showPromo && <CollaborationPromo />}
      <UnpublishConfirmationModal
        open={Boolean(unpublishViewLinkId)}
        viewLinkId={unpublishViewLinkId ?? ""}
        onClose={() => setUnpublishViewLinkId(null)}
      />
    </>
  );
};
